import { Link } from "react-router-dom";
import { ArrowLeft, Download, Calendar } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const TENANT = { id: "org_nexus_002", name: "Nexus Financial", plan: "Enterprise" };

const REQ_DATA = [
  { date: "Feb 1", random: 580000, vrf: 180000 }, { date: "Feb 3", random: 620000, vrf: 195000 },
  { date: "Feb 5", random: 540000, vrf: 160000 }, { date: "Feb 7", random: 710000, vrf: 220000 },
  { date: "Feb 9", random: 680000, vrf: 210000 }, { date: "Feb 11", random: 820000, vrf: 260000 },
  { date: "Feb 13", random: 750000, vrf: 235000 },
];

const LATENCY = [
  { range: "0-1ms", count: 180000 }, { range: "1-2ms", count: 520000 }, { range: "2-3ms", count: 410000 },
  { range: "3-4ms", count: 95000 }, { range: "4-5ms", count: 18000 }, { range: "5ms+", count: 3200 },
];

const BANDWIDTH = [
  { date: "Feb 1", gb: 38 }, { date: "Feb 3", gb: 42 }, { date: "Feb 5", gb: 35 },
  { date: "Feb 7", gb: 51 }, { date: "Feb 9", gb: 48 }, { date: "Feb 11", gb: 56 }, { date: "Feb 13", gb: 52 },
];

const SOURCES = [
  { name: "Raw QRNG", value: 25, color: "#06B6D4" },
  { name: "DRBG Expanded", value: 55, color: "#0F172A" },
  { name: "VRF Output", value: 20, color: "#64748B" },
];

const ENDPOINTS = [
  { endpoint: "/v1/random/bytes", calls: "4,521,000", bw: "168 GB", avg: "1.9ms", p99: "3.8ms", errors: "0.01%" },
  { endpoint: "/v1/vrf/generate", calls: "1,200,000", bw: "42 GB", avg: "3.6ms", p99: "6.8ms", errors: "0.02%" },
  { endpoint: "/v1/random/range", calls: "1,890,000", bw: "52 GB", avg: "1.7ms", p99: "3.2ms", errors: "0.00%" },
  { endpoint: "/v1/random/stream", calls: "620,000", bw: "45 GB", avg: "1.1ms", p99: "2.4ms", errors: "0.01%" },
  { endpoint: "/v1/health/status", calls: "201,100", bw: "0.1 GB", avg: "0.6ms", p99: "1.2ms", errors: "0.00%" },
];

const ERROR_LOG = [
  { time: "Feb 13 14:15", code: "429", endpoint: "/v1/random/bytes", message: "Rate limit exceeded", count: 12 },
  { time: "Feb 13 08:22", code: "400", endpoint: "/v1/vrf/generate", message: "Invalid input length", count: 3 },
  { time: "Feb 12 22:45", code: "503", endpoint: "/v1/random/stream", message: "Entropy pool temporarily unavailable", count: 1 },
  { time: "Feb 12 16:30", code: "429", endpoint: "/v1/random/bytes", message: "Rate limit exceeded", count: 8 },
];

export default function TenantUsage() {
  return (
    <div data-testid="tenant-usage-page">
      <div className="flex items-start justify-between mb-8">
        <div className="flex items-center gap-4">
          <Link to="/admin/tenants/org_nexus_002" className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors" data-testid="back-to-detail-btn">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="tenant-usage-title">Usage Analytics</h1>
              <Badge className="bg-slate-900 text-white text-[10px]">{TENANT.plan}</Badge>
            </div>
            <p className="text-sm text-slate-500 mt-0.5">{TENANT.name} &middot; <span className="font-mono">{TENANT.id}</span></p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select defaultValue="7d">
            <SelectTrigger className="w-40 h-9 text-xs" data-testid="usage-period-select">
              <Calendar className="w-3.5 h-3.5 mr-2 text-slate-400" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24h">Last 24 hours</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <button className="inline-flex items-center gap-2 border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs px-4 py-2" data-testid="export-usage-btn">
            <Download className="w-3.5 h-3.5" /> Export
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {[
          { label: "Total Requests", value: "8.43M", sub: "this period" },
          { label: "Bandwidth", value: "312 GB", sub: "of 500 GB limit" },
          { label: "Avg Latency", value: "2.1ms", sub: "p99: 4.6ms" },
          { label: "Error Rate", value: "0.012%", sub: "24 errors total" },
          { label: "Active Keys", value: "12", sub: "of 50 max" },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-slate-200 p-4" data-testid={`usage-summary-${i}`}>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider">{s.label}</div>
            <div className="text-xl font-bold text-slate-900 mt-0.5">{s.value}</div>
            <div className="text-[10px] text-slate-400">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        <div className="lg:col-span-2 bg-white border border-slate-200 p-6" data-testid="tenant-requests-chart">
          <h3 className="text-sm font-semibold text-slate-900 mb-1">Request Volume</h3>
          <p className="text-xs text-slate-500 mb-4">By service type</p>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={REQ_DATA}>
              <defs>
                <linearGradient id="tRandG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#06B6D4" stopOpacity={0.15} /><stop offset="95%" stopColor="#06B6D4" stopOpacity={0} /></linearGradient>
                <linearGradient id="tVrfG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0F172A" stopOpacity={0.15} /><stop offset="95%" stopColor="#0F172A" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ border: '1px solid #E2E8F0', borderRadius: 0, fontSize: 12 }} />
              <Area type="monotone" dataKey="random" stroke="#06B6D4" strokeWidth={2} fill="url(#tRandG)" name="Random API" />
              <Area type="monotone" dataKey="vrf" stroke="#0F172A" strokeWidth={2} fill="url(#tVrfG)" name="VRF API" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-slate-200 p-6" data-testid="tenant-source-chart">
          <h3 className="text-sm font-semibold text-slate-900 mb-1">Entropy Type</h3>
          <p className="text-xs text-slate-500 mb-4">Distribution</p>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart><Pie data={SOURCES} innerRadius={45} outerRadius={65} paddingAngle={2} dataKey="value">{SOURCES.map((e, i) => <Cell key={i} fill={e.color} />)}</Pie><Tooltip /></PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-3">
            {SOURCES.map((s, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2"><div className="w-2.5 h-2.5" style={{ backgroundColor: s.color }} /><span className="text-slate-600">{s.name}</span></div>
                <span className="font-mono text-slate-900 font-medium">{s.value}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <div className="bg-white border border-slate-200 p-6" data-testid="tenant-latency-chart">
          <h3 className="text-sm font-semibold text-slate-900 mb-1">Latency Distribution</h3>
          <p className="text-xs text-slate-500 mb-4">Response time histogram</p>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={LATENCY}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="range" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ border: '1px solid #E2E8F0', borderRadius: 0, fontSize: 12 }} />
              <Bar dataKey="count" fill="#06B6D4" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-slate-200 p-6" data-testid="tenant-bandwidth-chart">
          <h3 className="text-sm font-semibold text-slate-900 mb-1">Bandwidth (GB/day)</h3>
          <p className="text-xs text-slate-500 mb-4">Data transferred</p>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={BANDWIDTH}>
              <defs><linearGradient id="bwG2" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10B981" stopOpacity={0.15} /><stop offset="95%" stopColor="#10B981" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ border: '1px solid #E2E8F0', borderRadius: 0, fontSize: 12 }} />
              <Area type="monotone" dataKey="gb" stroke="#10B981" strokeWidth={2} fill="url(#bwG2)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Endpoint Breakdown */}
      <div className="bg-white border border-slate-200 mb-4" data-testid="tenant-endpoints-table">
        <div className="px-6 py-4 border-b border-slate-200"><h3 className="text-sm font-semibold text-slate-900">Endpoint Breakdown</h3></div>
        <div>
          <div className="grid grid-cols-[1fr_80px_90px_100px_80px_90px] bg-slate-50 border-b border-slate-200">
            {["Endpoint","Calls","Bandwidth","Avg Latency","P99","Error Rate"].map(h => (
              <div key={h} className="px-6 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</div>
            ))}
          </div>
          {ENDPOINTS.map((ep, i) => (
            <div key={i} className="grid grid-cols-[1fr_80px_90px_100px_80px_90px] border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`tenant-endpoint-${i}`}>
              <div className="px-6 py-3 font-mono text-xs text-slate-900 font-medium flex items-center">{ep.endpoint}</div>
              <div className="px-6 py-3 font-mono text-xs text-slate-600 flex items-center">{ep.calls}</div>
              <div className="px-6 py-3 font-mono text-xs text-slate-600 flex items-center">{ep.bw}</div>
              <div className="px-6 py-3 font-mono text-xs text-slate-600 flex items-center">{ep.avg}</div>
              <div className="px-6 py-3 font-mono text-xs text-slate-600 flex items-center">{ep.p99}</div>
              <div className="px-6 py-3 font-mono text-xs text-slate-600 flex items-center">{ep.errors}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Error Log */}
      <div className="bg-white border border-slate-200" data-testid="tenant-error-log">
        <div className="px-6 py-4 border-b border-slate-200"><h3 className="text-sm font-semibold text-slate-900">Recent Errors</h3></div>
        <div>
          <div className="grid grid-cols-[140px_80px_1fr_1fr_80px] bg-slate-50 border-b border-slate-200">
            {["Time","Code","Endpoint","Message","Count"].map(h => (
              <div key={h} className="px-6 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</div>
            ))}
          </div>
          {ERROR_LOG.map((err, i) => (
            <div key={i} className="grid grid-cols-[140px_80px_1fr_1fr_80px] border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`error-row-${i}`}>
              <div className="px-6 py-3 font-mono text-[11px] text-slate-500 flex items-center">{err.time}</div>
              <div className="px-6 py-3 flex items-center"><Badge variant="outline" className={`text-[10px] font-mono ${err.code === '429' ? 'bg-amber-50 text-amber-700 border-amber-200' : err.code === '503' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-slate-50 text-slate-600'}`}>{err.code}</Badge></div>
              <div className="px-6 py-3 font-mono text-xs text-slate-700 flex items-center">{err.endpoint}</div>
              <div className="px-6 py-3 text-xs text-slate-600 flex items-center">{err.message}</div>
              <div className="px-6 py-3 font-mono text-xs text-slate-600 flex items-center">{err.count}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
